'use client';

import { Icon } from '../ui/Icon';
import { Avatar, AvatarImage } from '@workspace/ui/components/avatar';
import { ProfileOptions } from './ProfileOptions';
import { Button } from '@workspace/ui/components/button';
import { ProfileNotifications } from './ProfileNotifications';
import { useBoolean } from '@/src/hooks/use-boolean';
import { cn } from '@workspace/ui/lib/utils';
import { CreateContentButton } from './CreateContentButton';
import { usePathname, useSearchParams } from 'next/navigation';
import { OnboardingBar } from './OnboardingBar';
import { IUIProfile } from '@/src/types/IUIProfile';
import { Modal } from '@/src/sections/social/components/Modal';
import { useGameBySlug } from '@/src/hooks/use-game-by-slug';
import { PostForm } from '@/src/sections/social/components/PostForm';
import { useNotificationsDot } from '@/src/hooks/use-notifications-dot';

type Props = {
  isHome?: boolean;
  profile?: IUIProfile;
};

export const HeaderActions = ({ isHome, profile }: Props) => {
  const pathname = usePathname();
  const isNotificationsOpen = useBoolean(false);
  const { hasNewDot, refreshDot } = useNotificationsDot(profile);

  const isPostPage = pathname.includes('/social-post/');
  const allowedPaths = [
    '/explore-stories',
    '/browse-projects',
    '/library',
    '/',
  ];
  const showCreateButton = allowedPaths.includes(pathname || '') || isPostPage;
  const isOpenModal = useBoolean(false);
  const isNotHome = pathname !== '/';

  const searchParams = useSearchParams();
  const gameSlug = searchParams.get('game') ?? '';
  const { data: game } = useGameBySlug(gameSlug, !!gameSlug);

  return (
    <>
      <div
        className={cn('flex', {
          'gap-3': !profile?.isOnboarded,
        })}
      >
        <div className="hidden md:flex">
          {!profile?.isOnboarded && profile && (
            <OnboardingBar profile={profile} />
          )}
        </div>
        <div className="flex items-center md:gap-4 gap-0 md:h-10">
          {showCreateButton && (
            <CreateContentButton
              profile={profile}
              isOpenModal={isOpenModal.onTrue}
            />
          )}
          <ProfileNotifications
            isNotificationsOpen={isNotificationsOpen}
            profile={profile!}
            onAllRead={refreshDot}
            onAnyRead={refreshDot}
          >
            <Button
              variant="ghost"
              className={cn(
                'flex p-3 hover:cursor-pointer transition-colors duration-200 ease-in-out relative border border-transparent hover:border-white md:h-10',
                {
                  'bg-black hover:bg-black/80': isNotificationsOpen.value,
                },
              )}
            >
              <Icon
                name="bell"
                className={cn('size-6', {
                  'text-white': isNotificationsOpen.value,
                })}
              />
              {hasNewDot.value && (
                <div
                  className={cn(
                    'absolute top-1.5 right-3 rounded-full size-2 bg-blue-primary',
                    {
                      'bg-pink-accent': isHome,
                    },
                  )}
                />
              )}
            </Button>
          </ProfileNotifications>

          <ProfileOptions profileLink={profile?.uniqueLink || ''}>
            <Button
              variant="ghost"
              className="flex md:h-11 items-center md:gap-2 gap-0.5 hover:bg-[#1E1E1E0D] rounded-[8px] h-fit md:p-1.5 p-0 hover:cursor-pointer border border-transparent hover:border-white"
            >
              <Avatar
                className={cn(
                  'md:size-7 size-6 flex justify-center items-center',
                  { 'border-black': isHome },
                )}
              >
                <AvatarImage
                  src={profile?.avatarUrl || '/images/default-avatar.png'}
                  alt="Profile"
                />
              </Avatar>
            </Button>
          </ProfileOptions>
        </div>
      </div>
      <Modal open={isOpenModal.value} isPost>
        <PostForm
          isNotHome={isNotHome}
          profile={profile}
          onCloseModal={isOpenModal.onFalse}
          game={game ? game : undefined}
        />
      </Modal>
    </>
  );
};
